import { useRef, useState } from 'react';
import './App.css';
import DrawingCanvas, { DRAWING_PEN_WIDTH } from "./DrawingCanvas"
import PredictionResults from './PredictionResults';
import { Button, Text, Title } from '@mantine/core';

const BRIQUE_GITHUB_URL = 'https://github.com/AlexisAoun/brique.rs';
const LINKEDIN_URL = 'https://linkedin.com/in/alexis-aoun-56601020b';

function App() {
  const ratioOfDrawingCanvasMobile = 0.8;
  const ratioOfDrawingCanvasDesktop = 0.25;
  const initialArray = new Float64Array([0.0]);
  const [probs, setProbs] = useState<Float64Array>(initialArray);
  const projectInfoRef = useRef<HTMLElement | null>(null);

  function scrollToProjectInfo(): void {
    projectInfoRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }

 function getDeviceViewportSize(): { width: number; height: number } {
    if (typeof window === "undefined") {
      // If running in a non-browser environment (SSR, etc.)
      return { width: 0, height: 0 };
    }
  
    return {
      width: window.innerWidth,
      height: window.innerHeight,
    };
  }

  function getDrawingCanvasWidth() {
    const size = getDeviceViewportSize();

    if(size.width < 1200) {
      return size.width * ratioOfDrawingCanvasMobile;
    } else {
      return size.width * ratioOfDrawingCanvasDesktop;
    }
  }

  //has to be a square, the model's input is a square image
  function getDrawingCanvasHeight() {
    return getDrawingCanvasWidth();
  }

  return (
    <div className="App">
      <header className="app-header">
        <nav className="social-links" aria-label="External links">
          <a
            className="social-link"
            href={BRIQUE_GITHUB_URL}
            target="_blank"
            rel="noreferrer"
            aria-label="Brique.rs on GitHub"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C6.422 18.07 5.633 17.7 5.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.237 1.838 1.237 1.07 1.835 2.809 1.305 3.495.998.108-.776.418-1.305.762-1.604-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.296-1.23 3.296-1.23.645 1.653.24 2.873.12 3.176.77.84 1.235 1.91 1.235 3.22 0 4.61-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222 0 1.606-.015 2.896-.015 3.286 0 .315.21.694.825.576C20.565 21.796 24 17.3 24 12.297c0-6.627-5.373-12-12-12" />
            </svg>
          </a>
          <a
            className="social-link"
            href={LINKEDIN_URL}
            target="_blank"
            rel="noreferrer"
            aria-label="LinkedIn"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V8.999h3.414v1.561h.046c.476-.9 1.637-1.85 3.37-1.85 3.6 0 4.267 2.37 4.267 5.455v6.287zM5.337 7.433a2.062 2.062 0 1 1 0-4.123 2.062 2.062 0 0 1 0 4.123zM7.119 20.452H3.554V8.999h3.565v11.453zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.454C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z" />
            </svg>
          </a>
        </nav>
        <Title order={1} className='main-title'>Brique.rs MNIST demo</Title>
        <Text className="app-subtitle">Draw a digit, then hit Predict</Text>
        <Button
          className="info-button"
          color="blue"
          variant="outline"
          size="lg"
          type="button"
          onClick={scrollToProjectInfo}
        >
          What is this
        </Button>
      </header>
      <div className="container">
        <DrawingCanvas width={getDrawingCanvasWidth()} height={getDrawingCanvasHeight()} penWidth={DRAWING_PEN_WIDTH} setProb={setProbs}/>
        <PredictionResults probs={probs}/>
      </div>
      <section
        ref={projectInfoRef}
        id="project-info"
        className="project-info"
        aria-labelledby="project-info-title"
      >
        <div className="project-info-header">
          <Text className="section-eyebrow">Under the hood</Text>
          <Title order={2} id="project-info-title" className="project-info-title">
            What is this?
          </Title>
          <Text className="project-info-intro">
            A small, end-to-end machine learning project: draw a digit, process it like MNIST,
            and run the prediction directly in your browser.
          </Text>
        </div>
        <div className="project-info-grid">
          <article className="info-card">
            <Text className="info-card-kicker">01</Text>
            <Title order={3} className="info-card-title">Built from scratch</Title>
            <Text className="info-card-copy">
              Brique.rs is my neural-network library, written from scratch in Rust without an
              existing machine-learning framework. The matrix math, MLP, training loop, and model
              serialization are all part of the project.
            </Text>
          </article>
          <article className="info-card">
            <Text className="info-card-kicker">02</Text>
            <Title order={3} className="info-card-title">Runs locally</Title>
            <Text className="info-card-copy">
              The trained model is compiled to WebAssembly and loaded in the browser, so your
              drawing stays on the page while the prediction is calculated locally.
            </Text>
          </article>
          <article className="info-card">
            <Text className="info-card-kicker">03</Text>
            <Title order={3} className="info-card-title">97%+ on MNIST</Title>
            <Text className="info-card-copy">
              The model reaches over 97% accuracy on the classic MNIST dataset. This demo formats
              each drawing into the same 28x28 style before sending it to the model.
            </Text>
          </article>
        </div>
      </section>
    </div>
  );
}

export default App;
